import { useEffect, useRef } from "react";

const CONFETTI_COLORS = ["#101828", "#30a46c", "#ffc53d", "#1964fe", "#e5e7eb", "#ffffff"];
type ConfettiContainer = Awaited<ReturnType<typeof import("@tsparticles/confetti")["confetti"]>>;

export function SetupConfetti({
  anchorRef,
}: {
  anchorRef: React.RefObject<HTMLDivElement | null>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const anchor = anchorRef.current;
    if (!canvas || !anchor) return;
    const anchorElement = anchor;

    let disposed = false;
    let container: ConfettiContainer;

    async function fire() {
      const { confetti } = await import("@tsparticles/confetti");
      const rect = anchorElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const y = ((rect.top + rect.height * 0.55) / viewportHeight) * 100;
      const leftX = ((rect.left + 18) / viewportWidth) * 100;
      const rightX = ((rect.right - 18) / viewportWidth) * 100;
      const localConfetti = await confetti.create(canvas, {
        count: 0,
        disableForReducedMotion: true,
        zIndex: 1,
      });
      if (disposed) {
        const initialContainer = await localConfetti({ count: 0 });
        initialContainer?.destroy(false);
        return;
      }

      const common = {
        colors: CONFETTI_COLORS,
        count: 46,
        decay: 0.91,
        disableForReducedMotion: true,
        gravity: 0.82,
        scalar: 0.72,
        shapes: ["square", "circle"],
        spread: 44,
        startVelocity: 30,
        ticks: 150,
        zIndex: 1,
      };
      const [leftContainer, rightContainer] = await Promise.all([
        localConfetti({ ...common, angle: 160, position: { x: leftX, y } }),
        localConfetti({ ...common, angle: 20, position: { x: rightX, y } }),
      ]);
      container = leftContainer ?? rightContainer;
      if (disposed) container?.destroy(false);
    }

    void fire();
    return () => {
      disposed = true;
      container?.destroy(false);
    };
  }, [anchorRef]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-[1] size-full"
      aria-hidden
    />
  );
}
